import { useCallback, useEffect, useRef, useState } from 'react';

interface UseChatAutoScrollOptions {
  requestsLength: number;
  requests: unknown[];
  isReceivingResponse: boolean;
  onScrollStateChange?: (isScrolled: boolean) => void;
}

const useChatAutoScroll = ({
  requestsLength,
  requests,
  isReceivingResponse,
  onScrollStateChange,
}: UseChatAutoScrollOptions) => {
  const chatContentRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const shouldAutoScrollRef = useRef(true);
  const previousRequestsLengthRef = useRef(0);

  // Idenitify if we are currently at the bottom of the scroll
  // and add a small threshold to allow scroll override.
  const isNearBottom = useCallback((element?: HTMLDivElement | null) => {
    if (!element) return true;
    const threshold = 24;
    return element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
  }, []);

  const scrollToBottom = useCallback((behavior?: ScrollBehavior) => {
    const element = chatContentRef.current;
    if (!element) return;
    requestAnimationFrame(() => {
      element.scrollTo({ top: element.scrollHeight, behavior });
    });
  }, []);

  // Always scroll to bottom when a new message is added (even if user was scrolled up).
  useEffect(() => {
    const currentLength = requestsLength;
    const previousLength = previousRequestsLengthRef.current;
    if (currentLength > previousLength) {
      shouldAutoScrollRef.current = true;
      scrollToBottom('smooth');
    }
    previousRequestsLengthRef.current = currentLength;
  }, [requestsLength, scrollToBottom]);

  // Keep streaming text visible at the bottom while allowing user override.
  // Also ensure citations are visible once streaming completes.
  useEffect(() => {
    if (!requestsLength) return;
    if (!shouldAutoScrollRef.current) return;
    scrollToBottom(isReceivingResponse ? undefined : 'smooth');
  }, [isReceivingResponse, requestsLength, requests, scrollToBottom]);

  // User scroll overrides auto-scroll until they return near bottom.
  const handleScroll = useCallback(() => {
    const element = chatContentRef.current;
    const nextIsScrolled = (element?.scrollTop ?? 0) > 0;
    setIsScrolled((prev) => (prev === nextIsScrolled ? prev : nextIsScrolled));
    shouldAutoScrollRef.current = isNearBottom(element);
  }, [isNearBottom]);

  useEffect(() => {
    onScrollStateChange?.(isScrolled);
  }, [isScrolled, onScrollStateChange]);

  return { chatContentRef, isScrolled, handleScroll };
};

export { useChatAutoScroll };
